import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import LaunchIcon from "@mui/icons-material/Launch";
import DownloadIcon from "@mui/icons-material/Download";
import ApartmentIcon from "@mui/icons-material/Apartment";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ArticleIcon from "@mui/icons-material/Article";

import { IconRefund } from "src/icons";

import {
  getTokenOrderById,
  uploadTokenOrderReceipt,
} from "src/api/tokenTransaction";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  Black,
  Blue,
  DarkGrey,
  Green,
  Grey,
  GreyStroke,
  White,
  White01,
} from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import Button from "src/components/core/Button";
import Chip from "src/components/core/Chip";
import UploadFile from "src/components/upload/dropzone/UploadFile";

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
`;

const Wrapper = styled(Box)`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const SpanTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.xl};
  font-weight: 700;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.lg};
  }
`;

const SpanSubTitle = styled.div`
  color: ${({ color }) => (color ? color : Grey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const SpanItem = styled.div`
  color: ${({ color }) => (color ? color : DarkGrey)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSummaryItem = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.lg};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 600)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.md};
  }
`;

const WrapperContent = styled(Wrapper)`
  padding: 32px;
  border-radius: 24px;
`;

const WrapperInfo = styled.div`
  width: 100%;
  padding: 16px;
  margin-bottom: 24px;
  border-radius: 16px;
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WrapperSellerInfo = styled.div`
  width: 100%;
  padding: 16px;
  margin-bottom: 24px;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TaxInvoiceInfo = styled.div`
  width: 100%;
  padding: 16px;
  background-color: #f7fbff;
  border: 1px solid #dbeafe;
  border-radius: 16px;
`;

const DocumentContainer = styled.div`
  width: 100%;
  padding: 24px;
  border-radius: 16px;
`;

const UploadDocument = styled(DocumentContainer)`
  position: relative;
  border: 1px dashed ${GreyStroke};
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DownloadDocument = styled(DocumentContainer)`
  border: 1px solid #dcfce7;
  background-color: #f0fdf4;
`;

const TokenOrderDetail = ({ open }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [uploadReceiptFile, setUploadReceiptFile] = useState(null);

  useEffect(() => {
    if (!orderId) return;

    fetchOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);

      const data = await getTokenOrderById(orderId);
      setOrderDetail(data);
    } catch (error) {
      console.error("Error fetching order detail:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadReceipt = async () => {
    try {
      setIsLoading(true);

      await uploadTokenOrderReceipt(orderId, uploadReceiptFile);
      enqueueSnackbar("อัปโหลดใบเสร็จสำเร็จ", { variant: "success" });
      setUploadReceiptFile(null);

      // After successful upload, you might want to refetch the order detail to get the updated receipt URL
      await fetchOrderDetail();
    } catch (error) {
      console.error("Error uploading receipt:", error);
      enqueueSnackbar("เกิดข้อผิดพลาดในการอัปโหลดใบเสร็จ", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Main open={open}>
        <Container>
          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item xs={12}>
              <Button
                variant="textdark"
                startIcon={<ArrowBackIosNewIcon />}
                onClick={() => navigate(-1)}
              >
                กลับไปหน้ารายการทั้งหมด
              </Button>
            </Grid>

            <Grid item xs={12}>
              <WrapperContent>
                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <SpanTitle>
                      {orderDetail?.user?.seller?.company_name}
                    </SpanTitle>
                    <SpanSubTitle>
                      {orderDetail?.order_no} • วันที่{" "}
                      {orderDetail?.createdAt
                        ? dayjs(orderDetail.createdAt).format(
                            "DD/MM/YYYY HH:mm",
                          )
                        : "-"}
                    </SpanSubTitle>
                  </Box>

                  <Chip
                    icon={
                      orderDetail?.status === "PAID" ? (
                        <CheckCircleOutlinedIcon />
                      ) : orderDetail?.status === "PENDING" ? (
                        <AccessTimeIcon />
                      ) : orderDetail?.status === "REFUNDED" ? (
                        <IconRefund fill="currentColor" />
                      ) : (
                        <HighlightOffIcon />
                      )
                    }
                    label={
                      orderDetail?.status === "PAID"
                        ? "ชำระเงินสำเร็จ"
                        : orderDetail?.status === "PENDING"
                          ? "รอดำเนินการ"
                          : orderDetail?.status === "REFUNDED"
                            ? "ขอคืนเงิน"
                            : orderDetail?.status === "CANCELLED"
                              ? "ยกเลิกการสั่งซื้อ"
                              : orderDetail?.status === "EXPIRED"
                                ? "รายการหมดอายุ"
                                : "ชำระเงินไม่สำเร็จ"
                    }
                    variant="secondary"
                    color={
                      orderDetail?.status === "PAID"
                        ? "success"
                        : orderDetail?.status === "PENDING"
                          ? "warning"
                          : orderDetail?.status === "REFUNDED"
                            ? "info"
                            : "error"
                    } // ?
                    sx={{
                      height: 36,
                      padding: "8px 12px",
                      "& .MuiChip-label": {
                        fontSize: font.size.md,
                      },
                    }}
                  />
                </Stack>

                <Divider sx={{ mx: "-32px", my: 4, borderColor: White01 }} />

                <Stack direction="row" justifyContent="space-between" gap={4}>
                  {/* --------------- LEFT --------------- */}
                  <Box sx={{ flex: 1 }}>
                    <SpanSubTitle color={DarkGrey} mb={12} fontWeight={600}>
                      รายละเอียดแพ็คเกจ
                    </SpanSubTitle>

                    <WrapperInfo>
                      <Stack direction="row" justifyContent="space-between">
                        <SpanItem>แพ็คเกจที่เลือก</SpanItem>

                        <SpanItem color={Black} fontWeight={600}>
                          {orderDetail?.package_snapshot?.name}
                        </SpanItem>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <SpanItem>เครดิตที่ได้รับ</SpanItem>

                        <SpanItem color={Green} fontWeight={600}>
                          {`+${orderDetail?.token_amount} เครดิต`}
                        </SpanItem>
                      </Stack>

                      <Divider sx={{ color: Grey }} />

                      <Stack direction="row" justifyContent="space-between">
                        <SpanSummaryItem>ยอดชำระสุทธิ</SpanSummaryItem>

                        <SpanSummaryItem>
                          {`${convertIntToCurrency(orderDetail?.price)} บาท`}
                        </SpanSummaryItem>
                      </Stack>
                    </WrapperInfo>

                    <SpanSubTitle color={DarkGrey} mb={12} fontWeight={600}>
                      ข้อมูลผู้ขาย
                    </SpanSubTitle>

                    <WrapperSellerInfo>
                      <Avatar />

                      <Box sx={{ flex: 1 }}>
                        <SpanItem color={Black} fontWeight={600}>
                          {orderDetail?.user?.seller?.company_name}
                        </SpanItem>
                        <SpanSubTitle>
                          Premium Seller • Verified Account
                        </SpanSubTitle>
                      </Box>

                      <IconButton
                        aria-label="view"
                        onClick={() =>
                          window.open(
                            `/seller/detail/${orderDetail?.user?.seller?._id}`,
                            "_blank",
                          )
                        }
                      >
                        <LaunchIcon sx={{ color: Blue }} />
                      </IconButton>
                    </WrapperSellerInfo>

                    <SpanSubTitle color={DarkGrey} mb={12} fontWeight={600}>
                      ข้อมูลสำหรับออกใบเสร็จ/ใบกำกับ
                    </SpanSubTitle>

                    <TaxInvoiceInfo>
                      <Stack
                        direction="row"
                        // alignItems="center"
                        spacing={1}
                      >
                        <ApartmentIcon sx={{ color: "#155DFC" }} />

                        <Box>
                          <Typography variant="subtitle1" marginBottom={0.5}>
                            {orderDetail?.billing?.name || "Name"}
                          </Typography>

                          {orderDetail?.billing?.is_company &&
                            checkNotEmpty(orderDetail?.billing?.tax_id) && (
                              <Typography
                                variant="body2"
                                marginBottom={0.5}
                                fontSize={font.size.sm}
                              >
                                {`TAX ID : ${orderDetail?.billing?.tax_id || "Tax ID not provided"}`}
                              </Typography>
                            )}

                          <Typography variant="body2" fontSize={font.size.sm}>
                            {orderDetail?.billing?.address ||
                              "Address not provided"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TaxInvoiceInfo>
                  </Box>

                  {/* --------------- RIGHT --------------- */}
                  <Box sx={{ flex: 1 }}>
                    <SpanSubTitle color={DarkGrey} mb={12} fontWeight={600}>
                      ข้อมูลการชำระเงิน
                    </SpanSubTitle>

                    <WrapperInfo>
                      <Stack direction="row" justifyContent="space-between">
                        <SpanItem>ช่องทาง</SpanItem>

                        <SpanItem color={Black} fontWeight={600}>
                          {orderDetail?.payment_method === "credit_card"
                            ? `Credit Card`
                            : orderDetail?.payment_method === "promptpay"
                              ? `PromptPay`
                              : orderDetail?.payment_method === "truewallet"
                                ? `TrueWallet`
                                : `Other`}
                        </SpanItem>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <SpanItem>เลขอ้างอิง</SpanItem>

                        <SpanItem color={Black}>
                          {orderDetail?.payment_ref
                            ? orderDetail.payment_ref
                            : "-"}
                        </SpanItem>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <SpanItem>เวลากดสั่งซื้อ</SpanItem>

                        <SpanItem color={Black} fontWeight={600}>
                          {orderDetail?.createdAt
                            ? dayjs(orderDetail.createdAt).format(
                                "DD/MM/YYYY HH:mm",
                              )
                            : "-"}
                        </SpanItem>
                      </Stack>
                    </WrapperInfo>

                    {orderDetail?.status === "PAID" && (
                      <>
                        <SpanSubTitle color={DarkGrey} mb={12} fontWeight={600}>
                          ใบเสร็จรับเงิน (Admin Upload)
                        </SpanSubTitle>

                        {!!orderDetail?.receipt_file_url &&
                        typeof orderDetail?.receipt_file_url === "string" ? (
                          <DownloadDocument>
                            <Stack direction="row" spacing={2}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  p: 1.5,
                                  backgroundColor: "#DCFCE7",
                                  borderRadius: "50%",
                                }}
                              >
                                <ArticleIcon sx={{ color: "#00A63E" }} />
                              </Box>

                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1">
                                  อัปโหลดใบเสร็จแล้ว
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={Grey}
                                  fontSize={font.size.sm}
                                >
                                  พร้อมให้ผู้ขายดาวน์โหลด
                                </Typography>
                              </Box>
                            </Stack>

                            <Button
                              fullWidth
                              variant="download"
                              color="secondary"
                              startIcon={<DownloadIcon />}
                              sx={{
                                mt: 2,
                                "&.MuiButton-download": {
                                  borderColor: "#B9F8CF",
                                  boxShadow:
                                    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
                                },
                              }}
                              onClick={() =>
                                window.open(
                                  orderDetail.receipt_file_url,
                                  "_blank",
                                )
                              }
                            >
                              ดาวน์โหลดใบเสร็จ (PDF)
                            </Button>
                          </DownloadDocument>
                        ) : (
                          <>
                            {checkNotEmpty(uploadReceiptFile) ? (
                              <UploadDocument>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: White,
                                    borderRadius: "50%",
                                    boxShadow:
                                      "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  <ArticleIcon sx={{ color: Grey }} />
                                </Box>

                                <Typography
                                  variant="subtitle1"
                                  textAlign="center"
                                  marginTop={1.5}
                                >
                                  {uploadReceiptFile?.name ||
                                    "uploaded filename"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  textAlign="center"
                                  color={Grey}
                                  fontSize={font.size.sm}
                                >
                                  {(
                                    uploadReceiptFile?.size /
                                    1024 /
                                    1024
                                  ).toFixed(2) || "uploaded filesize"}{" "}
                                  MB
                                </Typography>

                                <Stack direction="row" spacing={2} mt={3}>
                                  <Button
                                    variant="secondary"
                                    sx={{ minWidth: 100 }}
                                    onClick={() => setUploadReceiptFile(null)}
                                  >
                                    ลบ
                                  </Button>
                                  <Button
                                    variant="contained"
                                    sx={{ minWidth: 100 }}
                                    onClick={() => uploadReceipt()}
                                  >
                                    อัปโหลด
                                  </Button>
                                </Stack>
                              </UploadDocument>
                            ) : (
                              <UploadFile
                                callBackFile={(fileCallBlack) =>
                                  setUploadReceiptFile(fileCallBlack)
                                }
                                closeAlert={false}
                                acceptFileType={[".pdf"]}
                              >
                                <UploadDocument>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: White,
                                      borderRadius: "50%",
                                      boxShadow:
                                        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
                                    }}
                                  >
                                    <FileUploadIcon sx={{ color: Grey }} />
                                  </Box>

                                  <Typography
                                    variant="subtitle1"
                                    textAlign="center"
                                    marginTop={1.5}
                                  >
                                    อัปโหลดไฟล์ใบเสร็จ
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    textAlign="center"
                                    color={Grey}
                                    fontSize={font.size.sm}
                                  >
                                    ลากไฟล์มาวาง หรือคลิกเพื่อเลือก
                                  </Typography>
                                </UploadDocument>
                              </UploadFile>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </Stack>
              </WrapperContent>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default TokenOrderDetail;
